import type { ReactNode } from 'react';
import { useContext, useEffect, useReducer, useRef } from 'react';
import type { AgentProgress, WS } from '@trinity/chat';
import { reduceAgentEvent, createInitialAgentProgressState, QuerySocketEvent  } from '@trinity/chat';
import { QuerySocketContext } from '../Chat/context';
import { AgentProgressContext } from './context';
import { QueryClientActionContainer } from './QueryClientActionContainer';

type Props = {
  children: ReactNode;
  initialValue?: AgentProgress | undefined;
  queryIdentifier: string;
};

export const AgentProgressContainer = ({ children, initialValue, queryIdentifier }: Props) => {
  const [state, dispatch] = useReducer(
    reduceAgentEvent,
    undefined,
    () => initialValue ?? createInitialAgentProgressState('root_scope')
  );

  const socket = useContext(QuerySocketContext);

  const queryIdentifierRef = useRef(queryIdentifier);
  useEffect(() => {
    queryIdentifierRef.current = queryIdentifier;
  });

  useEffect(() => {
    if (!socket) return;

    const handleAgentProgress = (data: WS.QueryAgentProgress.Data) => {
      if (data.queryIdentifier !== queryIdentifierRef.current) return;
      dispatch(data.event);
    };

    socket.on(QuerySocketEvent.QueryAgentProgress, handleAgentProgress);

    return () => {
      socket.off(QuerySocketEvent.QueryAgentProgress, handleAgentProgress);
    };
  }, [socket]);

  return (
    <AgentProgressContext.Provider value={state}>
      <QueryClientActionContainer queryIdentifier={queryIdentifier}>
        {children}
      </QueryClientActionContainer>
    </AgentProgressContext.Provider>
  );
};
